import React, { useState, useEffect } from 'react';
import { Mafs, Coordinates, Plot, Point, Theme, Text } from 'mafs';

export const FunctionGraph = ({ config }) => {
    const { functions = [], highlights = [], viewbox = [-5, 5, -5, 5] } = config;
    const [minX, maxX, minY, maxY] = viewbox;

    const primaryFunc = functions[0] || { m: 1, c: 0, interactive: false };

    // State for interactive sliders
    const [m, setM] = useState(primaryFunc.m);
    const [c, setC] = useState(primaryFunc.c);

    // Sync state if config changes upstream
    useEffect(() => {
        setM(primaryFunc.m);
        setC(primaryFunc.c);
    }, [primaryFunc.m, primaryFunc.c]);

    const isInteractive = primaryFunc.interactive;

    return (
        <div className="flex flex-col gap-4 p-4 bg-white rounded-lg shadow-sm border border-slate-200">
            <div className="h-80 w-full relative">
                <Mafs
                    viewBox={{ x: [minX, maxX], y: [minY, maxY] }}
                    preserveAspectRatio={false}
                >
                    <Coordinates.Cartesian subdivisions={1} />

                    {/* Primary Function */}
                    <Plot.OfX
                        y={(x) => m * x + c}
                        color={primaryFunc.color || Theme.blue}
                        weight={3}
                    />

                    {/* Secondary Static Functions */}
                    {functions.slice(1).map((fn, idx) => (
                        <Plot.OfX
                            key={idx}
                            y={(x) => fn.m * x + fn.c}
                            color={fn.color || Theme.red}
                            style="dashed"
                            weight={2}
                        />
                    ))}

                    {/* Highlights */}
                    {highlights.map((pt, idx) => (
                        <g key={idx}>
                            <Point x={pt.x} y={pt.y} color={Theme.indigo} />
                            <Text
                                x={pt.x}
                                y={pt.y}
                                attach="sw"
                                size={12}
                                color={Theme.indigo}
                                weight="bold"
                            >
                                {pt.label}
                            </Text>
                        </g>
                    ))}
                </Mafs>
            </div>

            {/* Controls */}
            {isInteractive && (
                <div className="grid grid-cols-2 gap-4 bg-slate-50 p-3 rounded">
                    <div className="flex flex-col">
                        <label className="text-sm font-semibold text-slate-600 mb-1">Gradient (m): {m.toFixed(1)}</label>
                        <input
                            type="range"
                            min="-5" max="5" step="0.5"
                            value={m}
                            onChange={(e) => setM(parseFloat(e.target.value))}
                            className="accent-blue-600"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className="text-sm font-semibold text-slate-600 mb-1">Y-Intercept (c): {c.toFixed(1)}</label>
                        <input
                            type="range"
                            min="-5" max="5" step="0.5"
                            value={c}
                            onChange={(e) => setC(parseFloat(e.target.value))}
                            className="accent-blue-600"
                        />
                    </div>
                    <div className="col-span-2 text-center text-lg font-mono text-blue-700 bg-blue-50 py-1 rounded">
                        y = {m.toFixed(1)}x {c >= 0 ? '+' : '-'} {Math.abs(c).toFixed(1)}
                    </div>
                </div>
            )}
        </div>
    );
};